import type {
  BusinessUnit,
  BusinessUnitConversationTaggingConfig,
  InsuranceBrokerCredentials,
  InsuranceCredentialsResponse,
} from 'services/types/businessUnit';
import type { TemplateResponse } from 'services/types/message';

import baseApi from './baseApi';

export interface ServiceCenter {
  name: string;
  address: string | null;
}

export interface BranchOfficeDetail {
  name: string;
  address: string;
  mapsUrl?: string;
  wazeUrl?: string;
  phone?: string;
  hours: string[];
  advisorName?: string;
  advisorRole?: string;
  advisorEmail?: string;
}

export type HeaderComponent = {
  type: 'HEADER';
  text?: string;
  format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  example?: {
    header_handle?: string[];
  };
};

export type BodyComponent = {
  type: 'BODY';
  text: string;
  example?: {
    body_text?: string[][];
    body_text_named_params?: Array<{
      param_name: string;
      example: string;
    }>;
  };
};

export type FooterComponent = {
  type: 'FOOTER';
  text: string;
};

export type WhatsAppButton = { type: 'QUICK_REPLY'; text: string };
export type ButtonsComponent = {
  type: 'BUTTONS';
  buttons: Array<WhatsAppButton>;
};

export type TemplateComponent = HeaderComponent | BodyComponent | FooterComponent | ButtonsComponent;

export interface TemplateCreate {
  name: string;
  category: 'UTILITY' | 'MARKETING';
  language: 'es';
  parameter_format: 'named';
  components: TemplateComponent[];
}

const businessUnitsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBusinessUnitTemplates: build.query({
      query: (id: number) => ({
        url: `/businessUnits/${id}/templates`,
        method: 'GET',
      }),
      transformResponse: (response: TemplateResponse) => response,
      providesTags: (_result, _error, businessUnitId) => [{ type: 'BusinessUnitTemplates', id: businessUnitId }],
    }),
    createBusinessUnitTemplate: build.mutation({
      query: (props: { businessUnitId: number; template: TemplateCreate }) => ({
        url: `businessUnits/${props.businessUnitId}/template`,
        method: 'POST',
        body: props.template,
      }),
      transformResponse: (response: TemplateResponse) => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'BusinessUnitTemplates', id: props.businessUnitId }],
    }),
    deleteBusinessUnitTemplate: build.mutation({
      query: (props: { businessUnitId: number; name: string }) => ({
        url: `businessUnits/${props.businessUnitId}/template`,
        method: 'DELETE',
        params: {
          name: props.name,
        },
      }),
      transformResponse: (response: { success: boolean }) => response,
      invalidatesTags: (_result, _error, props) => [{ type: 'BusinessUnitTemplates', id: props.businessUnitId }],
    }),
    requestTemplateDeletion: build.mutation({
      query: (props: { businessUnitId: number; templateName: string }) => ({
        url: `businessUnits/${props.businessUnitId}/template-deletion-request`,
        method: 'POST',
        body: { templateName: props.templateName },
      }),
      transformResponse: (response: { success: boolean; ticketId: number }) => response,
    }),
    uploadTemplateFile: build.mutation({
      query: (props: { businessUnitId: number; file: File }) => {
        const formData = new FormData();
        formData.append('file', props.file);
        return {
          url: `businessUnits/${props.businessUnitId}/template-file`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { url: string }) => response,
    }),
    getUserBusinessUnits: build.query({
      query: () => ({
        url: '/businessUnits/currentUserBusinessUnits',
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit[]): BusinessUnit[] => response,
      providesTags: [{ type: 'BusinessUnit', id: 'LIST' }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnit: build.query<BusinessUnit, number>({
      query: (businessUnitId) => ({
        url: `/businessUnits/businessUnit/${businessUnitId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, businessUnitId) => [{ type: 'BusinessUnit', id: businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    updateBusinessUnitTaggingConfig: build.mutation<
      BusinessUnit,
      { businessUnitId: number; conversationTaggingConfig: BusinessUnitConversationTaggingConfig | null }
    >({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/tagging-config`,
        method: 'PATCH',
        body: { conversationTaggingConfig: props.conversationTaggingConfig },
      }),
      invalidatesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
    }),
    getBusinessUnitSources: build.query({
      query: (props: { businessUnitId: number | null }) => ({
        url: `/businessUnits/businessUnit/sources/${props.businessUnitId || ''}`,
        method: 'GET',
      }),
      transformResponse: (response: string[]): string[] => response,
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId ?? 'LIST' }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitAllowedBrands: build.query<string[], { businessUnitId: number }>({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/allowed-brands`,
        method: 'GET',
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitBranchOffices: build.query<string[], { businessUnitId: number; brands?: string[] }>({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/branch-offices`,
        method: 'GET',
        params: props.brands && props.brands.length > 0 ? { brands: props.brands.join(',') } : undefined,
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitServiceCenters: build.query<ServiceCenter[], { businessUnitId: number; brands?: string[] }>({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/service-centers`,
        method: 'GET',
        params: props.brands && props.brands.length > 0 ? { brands: props.brands.join(',') } : undefined,
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitBranchOfficeDetails: build.query<
      BranchOfficeDetail[],
      { businessUnitId: number; brands?: string[] }
    >({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/branch-offices`,
        method: 'GET',
        params: {
          detailed: 'true',
          ...(props.brands && props.brands.length > 0 ? { brands: props.brands.join(',') } : {}),
        },
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitServiceCenterDetails: build.query<
      BranchOfficeDetail[],
      { businessUnitId: number; brands?: string[] }
    >({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/service-centers`,
        method: 'GET',
        params: {
          detailed: 'true',
          ...(props.brands && props.brands.length > 0 ? { brands: props.brands.join(',') } : {}),
        },
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnit', id: props.businessUnitId }],
      keepUnusedDataFor: 3600,
    }),
    getBusinessUnitInsuranceCredentials: build.mutation({
      query: (businessUnitId: number) => ({
        url: `/businessUnits/businessUnit/${businessUnitId}/insurance-credentials`,
        method: 'GET',
      }),
      transformResponse: (response: InsuranceCredentialsResponse): InsuranceCredentialsResponse => response,
    }),
    updateBusinessUnitInsuranceCredentials: build.mutation({
      query: (props: { businessUnitId: number; insuranceBrokerCredentials: InsuranceBrokerCredentials | null }) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/insurance-credentials`,
        method: 'PATCH',
        body: { insuranceBrokerCredentials: props.insuranceBrokerCredentials },
      }),
      transformResponse: (response: InsuranceCredentialsResponse): InsuranceCredentialsResponse => response,
    }),
    validateInsuranceCredentials: build.mutation<
      { success: boolean },
      { businessUnitId: number; company: string; username: string; password: string }
    >({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/validate-insurance-credentials`,
        method: 'POST',
        body: { company: props.company, username: props.username, password: props.password },
      }),
      transformResponse: (response: { success: boolean }): { success: boolean } => response,
    }),
    getBusinessUnitHolidays: build.query<BusinessUnitHoliday[], { businessUnitId: number }>({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/holidays`,
        method: 'GET',
      }),
      providesTags: (_result, _error, props) => [{ type: 'BusinessUnitHolidays', id: props.businessUnitId }],
    }),
    createBusinessUnitHoliday: build.mutation<
      BusinessUnitHoliday,
      { businessUnitId: number; date: string; name: string }
    >({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/holidays`,
        method: 'POST',
        body: { date: props.date, name: props.name },
      }),
      invalidatesTags: (_result, _error, props) => [{ type: 'BusinessUnitHolidays', id: props.businessUnitId }],
    }),
    deleteBusinessUnitHoliday: build.mutation<void, { businessUnitId: number; holidayId: number }>({
      query: (props) => ({
        url: `/businessUnits/businessUnit/${props.businessUnitId}/holidays/${props.holidayId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, props) => [{ type: 'BusinessUnitHolidays', id: props.businessUnitId }],
    }),
  }),
  overrideExisting: false,
});

export interface BusinessUnitHoliday {
  id: number;
  businessUnitId: number;
  date: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export { businessUnitsApi };

export const {
  useGetBusinessUnitTemplatesQuery,
  useCreateBusinessUnitTemplateMutation,
  useDeleteBusinessUnitTemplateMutation,
  useRequestTemplateDeletionMutation,
  useUploadTemplateFileMutation,
  useGetUserBusinessUnitsQuery,
  useGetBusinessUnitQuery,
  useUpdateBusinessUnitTaggingConfigMutation,
  useGetBusinessUnitSourcesQuery,
  useGetBusinessUnitAllowedBrandsQuery,
  useGetBusinessUnitBranchOfficesQuery,
  useGetBusinessUnitServiceCentersQuery,
  useGetBusinessUnitBranchOfficeDetailsQuery,
  useGetBusinessUnitServiceCenterDetailsQuery,
  useGetBusinessUnitInsuranceCredentialsMutation,
  useUpdateBusinessUnitInsuranceCredentialsMutation,
  useValidateInsuranceCredentialsMutation,
  useGetBusinessUnitHolidaysQuery,
  useCreateBusinessUnitHolidayMutation,
  useDeleteBusinessUnitHolidayMutation,
} = businessUnitsApi;
